// src/pages/SupplierList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axios';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  LocalShipping as LocalShippingIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function SupplierList() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('companyId');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;

    setLoading(true);
    axios
      .get(`/Supplier/by-company/${companyId}`)
      .then((res) => {
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('خطأ في تحميل الموردين:', err);
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  const deleteSupplier = (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المورد؟')) return;

    axios
      .delete(`/Supplier/${id}`)
      .then(() => {
        setSuppliers((current) => current.filter((supplier) => supplier.id !== id));
        alert('تم حذف المورد بنجاح');
      })
      .catch(() => alert('حدث خطأ أثناء حذف المورد'));
  };

  const exportToExcel = () => {
    const data = suppliers.map((supplier, index) => ({
      'رقم': index + 1,
      'الاسم': supplier.name,
      'الرقم الضريبي': supplier.taxNumber || '-',
      'الجوال': supplier.mobile || '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'الموردين');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    saveAs(
      new Blob([output], { type: 'application/octet-stream' }),
      `suppliers_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 3, px: { xs: 1, sm: 2 } }} dir="rtl">
      {/* Header - consistent with the compact theme used on other pages */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          px: { xs: 1, sm: 2 },
          py: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <LocalShippingIcon sx={{ fontSize: 26, color: 'primary.main' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            إدارة الموردين
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
            ({suppliers.length})
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={exportToExcel}
            startIcon={<DownloadIcon />}
          >
            تصدير Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/Supplier/new')}
            startIcon={<PersonAddIcon />}
          >
            إضافة مورد
          </Button>
        </Box>
      </Box>

      {/* Suppliers table */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflowX: 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['#', 'الاسم', 'الرقم الضريبي', 'الجوال', 'إجراءات'].map((heading) => (
                  <TableCell
                    key={heading}
                    align="center"
                    sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier, index) => (
                <TableRow key={supplier.id} hover>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {supplier.name}
                  </TableCell>
                  <TableCell align="center">{supplier.taxNumber || '-'}</TableCell>
                  <TableCell align="center">{supplier.mobile || '-'}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="تعديل المورد">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/Supplier/${supplier.id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="حذف المورد">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteSupplier(supplier.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {suppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    لا يوجد موردون لعرضهم
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Container>
  );
}
